import React, { useEffect, useState } from 'react';
import {
    SimpleForm,
    required,
    email as validateEmail,
    useNotify,
    useRedirect,
    useDataProvider,
    Title,
} from 'react-admin';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Divider,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import BookIcon from '@mui/icons-material/Book';
import { API_URL } from '../config';
import { JumpstartTextInput, JumpstartSelectInput, JumpstartButton } from '../components/JumpstartInputs';
import { useT } from '../i18n/useT';

interface ProcessingFormData {
    sku: string;
    name: string;
    surname: string;
    address: string;
    telephone_number: string;
    email: string;
}

type PublicationChoice = {
    id: number;
    sku: string;
    title: string;
    display_name: string;
};

type FileRecord = {
    publication_id: number;
    file_type: string;
};

const queueProcessingRequest = async (
    data: ProcessingFormData,
    documentType: 'pdf' | 'epub'
) => {
    const token = localStorage.getItem('token');
    if (!token) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${API_URL}/queue/sign`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            processing_type: 'watermark',
            document_type: documentType,
            sku: data.sku,
            consumer: {
                name: data.name,
                surname: data.surname,
                address: data.address,
                telephone_number: data.telephone_number,
                email: data.email,
            },
        }),
    });

    const result = await response.json();
    if (!response.ok || result.status !== 1 || !result.queue_id) {
        throw new Error(result.detail || result.error_message || 'Failed to queue document');
    }

    return result.queue_id as string;
};

// Common consumer form fields
const ConsumerFields = () => {
    const t = useT();
    return (
        <>
            <JumpstartTextInput source="name" label={t('resources.queue.form.firstName')} validate={[required()]} fullWidth />
            <JumpstartTextInput source="surname" label={t('resources.queue.form.lastName')} validate={[required()]} fullWidth />
            <JumpstartTextInput source="address" label={t('resources.queue.form.address')} validate={[required()]} fullWidth multiline />
            <JumpstartTextInput source="telephone_number" label={t('resources.queue.form.telephone')} validate={[required()]} fullWidth />
            <JumpstartTextInput source="email" label={t('resources.queue.form.email')} validate={[required(), validateEmail()]} fullWidth />
        </>
    );
};

const useAvailablePublicationChoices = (
    documentType: 'pdf' | 'epub',
    notify: ReturnType<typeof useNotify>,
    dataProvider: ReturnType<typeof useDataProvider>
) => {
    const [publicationChoices, setPublicationChoices] = useState<PublicationChoice[]>([]);
    const [loadingPublications, setLoadingPublications] = useState(false);

    useEffect(() => {
        let active = true;

        const loadPublications = async () => {
            setLoadingPublications(true);

            try {
                const [{ data: publications }, { data: files }] = await Promise.all([
                    dataProvider.getList('publications', {
                        pagination: { page: 1, perPage: 10000 },
                        sort: { field: 'sku', order: 'ASC' },
                        filter: {},
                    }),
                    dataProvider.getList('files', {
                        pagination: { page: 1, perPage: 10000 },
                        sort: { field: 'id', order: 'ASC' },
                        filter: {},
                    }),
                ]);

                if (!active) {
                    return;
                }

                const publicationIdsWithFileType = new Set(
                    (files as FileRecord[])
                        .filter(file => file.file_type === documentType)
                        .map(file => file.publication_id)
                );

                setPublicationChoices(
                    publications
                        .filter((publication: any) => publicationIdsWithFileType.has(publication.id))
                        .map((publication: any) => ({
                            id: publication.id,
                            sku: publication.sku,
                            title: publication.title,
                            display_name: `${publication.sku} - ${publication.title}`,
                        }))
                );
            } catch (error) {
                console.error(`Failed to load publications for ${documentType} processing:`, error);

                if (active) {
                    notify('processing.notifications.publicationsLoadFailed', { type: 'warning' });
                }
            } finally {
                if (active) {
                    setLoadingPublications(false);
                }
            }
        };

        loadPublications();

        return () => {
            active = false;
        };
    }, [dataProvider, documentType, notify]);

    return { publicationChoices, loadingPublications };
};

const PublicationFields = ({
    publicationChoices,
    loadingPublications,
    disabled,
    documentType,
}: {
    publicationChoices: PublicationChoice[];
    loadingPublications: boolean;
    disabled: boolean;
    documentType: 'pdf' | 'epub';
}) => {
    const t = useT();
    return (
        <JumpstartSelectInput
            source="sku"
            label={t('processing.publicationLabel')}
            choices={publicationChoices}
            optionText="display_name"
            optionValue="sku"
            disabled={disabled || loadingPublications}
            fullWidth
            validate={[required()]}
            helperText={t(documentType === 'pdf' ? 'processing.pdf.publicationHelper' : 'processing.epub.publicationHelper')}
        />
    );
};

// PDF Watermark from database file
export const PDFWatermarkDatabase = () => {
    const t = useT();
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();
    const [processing, setProcessing] = useState(false);
    const { publicationChoices, loadingPublications } = useAvailablePublicationChoices('pdf', notify, dataProvider);

    const handleSubmit = async (data: Record<string, any>) => {
        const formData = data as ProcessingFormData;
        const resolvedSku = typeof formData.sku === 'string' ? formData.sku.trim() : '';

        if (!resolvedSku) {
            notify('processing.notifications.selectPublication', { type: 'error' });
            return;
        }

        setProcessing(true);

        try {
            const queueId = await queueProcessingRequest({
                ...formData,
                sku: resolvedSku,
            }, 'pdf');
            notify(t('processing.pdf.notifications.queuedSuccess', { queueId }), { type: 'success' });
            redirect('/queue');
        } catch (error: any) {
            notify(`${t('processing.pdf.notifications.failedPrefix')}: ${error.message}`, { type: 'error' });
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Card>
            <Title title={t('processing.pdf.title')} />
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    {t('processing.pdf.heading')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                    {t('processing.pdf.description')}
                </Typography>
                <Divider style={{ margin: '1rem 0' }} />
                <SimpleForm onSubmit={handleSubmit} toolbar={<Box />}>
                    <PublicationFields
                        publicationChoices={publicationChoices}
                        loadingPublications={loadingPublications}
                        disabled={processing}
                        documentType="pdf"
                    />
                    <Divider style={{ margin: '1rem 0', width: '100%' }} />
                    <Typography variant="subtitle1" gutterBottom style={{ width: '100%' }}>
                        {t('processing.consumerSection')}
                    </Typography>
                    <ConsumerFields />
                    <Box mt={2} width="100%">
                        <Typography variant="body2" color="textSecondary">
                            {t('processing.asyncNote')}
                        </Typography>
                    </Box>
                    <Box mt={2} pl={2.5} pb={2.5}>
                        <JumpstartButton
                            nativeType="submit"
                            disabled={processing}
                            label={processing ? t('processing.pdf.actionLoadingLabel') : t('processing.pdf.actionLabel')}
                        />
                    </Box>
                </SimpleForm>
            </CardContent>
        </Card>
    );
};

// EPUB Sign from database file
export const EPUBSignDatabase = () => {
    const t = useT();
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();
    const [processing, setProcessing] = useState(false);
    const { publicationChoices, loadingPublications } = useAvailablePublicationChoices('epub', notify, dataProvider);

    const handleSubmit = async (data: Record<string, any>) => {
        const formData = data as ProcessingFormData;
        const resolvedSku = typeof formData.sku === 'string' ? formData.sku.trim() : '';

        if (!resolvedSku) {
            notify('processing.notifications.selectPublication', { type: 'error' });
            return;
        }

        setProcessing(true);

        try {
            const queueId = await queueProcessingRequest({
                ...formData,
                sku: resolvedSku,
            }, 'epub');
            notify(t('processing.epub.notifications.queuedSuccess', { queueId }), { type: 'success' });
            redirect('/queue');
        } catch (error: any) {
            notify(`${t('processing.epub.notifications.failedPrefix')}: ${error.message}`, { type: 'error' });
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Card>
            <Title title={t('processing.epub.title')} />
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    {t('processing.epub.heading')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                    {t('processing.epub.description')}
                </Typography>
                <Divider style={{ margin: '1rem 0' }} />
                <SimpleForm onSubmit={handleSubmit} toolbar={<Box />}>
                    <PublicationFields
                        publicationChoices={publicationChoices}
                        loadingPublications={loadingPublications}
                        disabled={processing}
                        documentType="epub"
                    />
                    <Divider style={{ margin: '1rem 0', width: '100%' }} />
                    <Typography variant="subtitle1" gutterBottom style={{ width: '100%' }}>
                        {t('processing.consumerSection')}
                    </Typography>
                    <ConsumerFields />
                    <Box mt={2} width="100%">
                        <Typography variant="body2" color="textSecondary">
                            {t('processing.asyncNote')}
                        </Typography>
                    </Box>
                    <Box mt={2} pl={2.5} pb={2.5}>
                        <JumpstartButton
                            nativeType="submit"
                            disabled={processing}
                            label={processing ? t('processing.epub.actionLoadingLabel') : t('processing.epub.actionLabel')}
                        />
                    </Box>
                </SimpleForm>
            </CardContent>
        </Card>
    );
};

export const pdfWatermarkDatabaseIcon = PictureAsPdfIcon;
export const epubSignDatabaseIcon = BookIcon;
